import { useState, type ReactNode } from 'react';
import { AtSign, History, Mail, SlidersHorizontal } from 'lucide-react';
import { useEmailSource, type EmailSourceState } from './use-email-source';
import { EmailAddressTab } from './EmailAddressTab';
import { EmailSettings } from './EmailSettings';
import { EmailMessages } from './EmailMessages';
import './email-sheet.css';

type Tab = 'address' | 'settings' | 'recent';

const tabs: { id: Tab; label: string; icon: typeof Mail }[] = [
  { id: 'address', label: 'Address', icon: AtSign },
  { id: 'settings', label: 'Settings', icon: SlidersHorizontal },
  { id: 'recent', label: 'Recent messages', icon: History },
];

/**
 * "Add pages ⋯ → Email" (Dustin, 2026-09-24): the one place for a job's email, laid out like
 * CapturePoint's own dialogs. An engraved banner with the switch, then tabs down the left: the
 * address with how it works in pictures, the settings, and what arrived.
 */
export function EmailSheet(): ReactNode {
  const email = useEmailSource();
  const [tab, setTab] = useState<Tab>('address');
  return (
    <div className="paige-email-sheet">
      <Banner email={email} />
      <div className="paige-email-sheet-body">
        <nav className="paige-email-sheet-tabs" aria-label="Email">
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              aria-current={tab === id ? 'page' : undefined}
              onClick={() => setTab(id)}
            >
              <Icon size={17} aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>
        <div className="paige-email-sheet-pane">
          <Pane tab={tab} email={email} />
          {email.error && (
            <p className="paige-email-sheet-error" role="alert">
              {email.error}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

/** The engraved banner: what this is, and the switch that turns email on or off for the job. */
function Banner({ email }: { email: EmailSourceState }): ReactNode {
  const source = email.source;
  return (
    <header className="paige-email-sheet-banner">
      <span className="paige-email-sheet-mark" aria-hidden="true">
        <Mail size={22} />
      </span>
      <span className="paige-email-sheet-title">
        <h2 id="paige-intake-title">Email documents in</h2>
        <small>This job's own address. What's emailed to it arrives here.</small>
      </span>
      {source && (
        <label className="paige-email-sheet-onoff">
          <span>{source.enabled ? 'On' : 'Off'}</span>
          <input
            type="checkbox"
            role="switch"
            aria-label="Take documents by email"
            checked={source.enabled}
            disabled={email.saving}
            onChange={(event) => void email.save({ enabled: event.currentTarget.checked })}
          />
        </label>
      )}
    </header>
  );
}

function Pane({ tab, email }: { tab: Tab; email: EmailSourceState }): ReactNode {
  const source = email.source;
  if (source === undefined)
    return <p className="paige-email-sheet-quiet">Getting this job's address…</p>;
  if (source === null)
    return <p className="paige-email-sheet-quiet">This job has no email address yet.</p>;
  if (tab === 'settings')
    return <EmailSettings source={source} saving={email.saving} onSave={email.save} />;
  if (tab === 'recent') return <EmailMessages sourceId={source.id} />;
  return (
    <EmailAddressTab source={source} saving={email.saving} onRelease={() => void email.release()} />
  );
}
